import { networkType } from "@/config";
import { formatEther } from "ethers";

const SUBGRAPH_URL =
  "https://api.studio.thegraph.com/query/103520/spon-staking/v0.5.0";

const PROTOCOL_SUBGRAPH_URL =
  networkType === "mainnet"
    ? "https://api.goldsky.com/api/public/project_cm16apa2e540c01wz70x34xzf/subgraphs/sph-base-mainnet/1.0.3/gn"
    : "https://api.goldsky.com/api/public/project_cm16apa2e540c01wz70x34xzf/subgraphs/sph-base-sepolia-new/1.0.0/gn";

interface Json {
  [x: string]: string | number | boolean | Date | Json | JsonArray;
}
type JsonArray = Array<string | number | boolean | Date | Json | JsonArray>;

export const fetchSubgraphData = async <T = unknown>(
  query: string,
  variables: Json = {},
  options: { subgraphUrl: string } = { subgraphUrl: SUBGRAPH_URL }
): Promise<T> => {
  try {
    const response = await fetch(options.subgraphUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query, variables }),
      cache: "no-store",
    });
    const data = await response.json();
    if (data.errors) {
      throw new Error(JSON.stringify(data.errors));
    }
    return data.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const fetchProtocolSubgraphData = async <T = unknown>(
  query: string,
  variables: Json = {},
  options: { subgraphUrl: string } = { subgraphUrl: PROTOCOL_SUBGRAPH_URL }
): Promise<T> => {
  try {
    const response = await fetch(options.subgraphUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query, variables }),
      cache: "no-store",
    });
    const data = await response.json();
    if (data.errors) {
      throw new Error(JSON.stringify(data.errors));
    }
    return data.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getWithdrawalRequestsForAddressQuery = `query ($id: ID){
  users(where: {id: $id}) {
    id
    depositBalance
    isActive
    deposits {
      amount
      timestamp
    }
    withdrawRequests {
      id
      transactionHash
      timestamp
    }
    withdraws {
      id
      transactionHash
    }
  }
}`;

const getPoolDataQuery = `
  query($first: Int!) {
    thawingPoolStats(id: "global") {
      totalDeposits
      totalStakers
      activeStakers
    }
    users(first: $first, orderBy: depositBalance, orderDirection: desc) {
      id
      depositBalance
      isActive
      deposits {
        amount
        timestamp
      }
      withdrawRequests {
        id
        transactionHash
      }
      withdraws {
        id
        transactionHash
      }
    }
    transactions(first: 20, orderBy: timestamp, orderDirection: desc) {
      id
      from {
        id
      }
      eventType
      timestamp
    }
  }
`;

interface SubgraphUserData {
  users: {
    id: string;
    depositBalance: string;
    isActive: boolean;
    deposits: {
      amount: string;
      timestamp: string;
    }[];
    withdrawRequests: {
      id: string;
      transactionHash: string;
      timestamp: string;
    }[];
    withdraws: { id: string; transactionHash: string }[];
  }[];
}

export async function getWithdrawalRequestsForAddress(address: string) {
  const response = await fetchSubgraphData<SubgraphUserData>(
    getWithdrawalRequestsForAddressQuery,
    { id: address.toLowerCase() }
  );

  return {
    ...response.users[0],
    withdrawRequests: response.users[0].withdrawRequests.sort(
      (a, b) => Number(a.timestamp) - Number(b.timestamp)
    ),
  };
}

interface SubgraphPoolData {
  thawingPoolStats: {
    activeStakers: number;
    totalStakers: number;
    totalDeposits: number;
  };
  users: {
    id: string;
    depositBalance: string;
    isActive: boolean;
    deposits: {
      amount: string;
      timestamp: string;
    }[];
    withdrawRequests: {
      id: string;
      transactionHash: string;
      timestamp: string;
    }[];
    withdraws: { id: string; transactionHash: string }[];
  }[];
}

export async function getPoolData() {
  const FIRST = 100;
  const response = await fetchSubgraphData<SubgraphPoolData>(getPoolDataQuery, {
    first: FIRST,
  });

  const activeUsers = response.users.filter((user) => user.isActive);

  const totalDepositBalance = activeUsers.reduce(
    (sum, user) => sum + BigInt(user.depositBalance),
    BigInt(0)
  );

  return {
    totalDepositBalance: Number(formatEther(totalDepositBalance)),
    activeStakers: activeUsers.length,
  };
}

const getFizzNodesByWalletQuery = `
  query ($walletAddress: String!) {
    fizzNodes(where: {walletAddress: $walletAddress}) {
      fizzId
      walletAddress
      spec
      attributes {
        id
        category
        units
      }
      providerId
      provider {
        spec
        walletAddress
      }
      status
      joinTimestamp
      rewardWallet
      isDenied
      attributes {
        category
        attributeId
        units
      }
      rewardPerEra
      pendingRewards
      lastClaimedEra
      totalLeases
      activeLeases
      earnings {
        earned
        balance
        token {
          name
        }
      }
      slashedAmount
      slashedEras
      isPaused
    }
  }
`;

type FizzNodeAttribute = {
  id: string;
  category: string;
  units: string;
  attributeId?: string;
};

type FizzNodeEarning = {
  earned: string;
  balance: string;
  token: {
    name: string;
  };
};

export type FizzNode = {
  fizzId: string;
  walletAddress: string;
  spec: string;
  attributes: FizzNodeAttribute[];
  providerId: string;
  provider: {
    spec: string;
    walletAddress: string;
  };
  status: string;
  joinTimestamp: string;
  rewardWallet: string;
  isDenied: boolean;
  rewardPerEra: string;
  pendingRewards: string;
  lastClaimedEra: string;
  totalLeases: string;
  activeLeases: string;
  earnings: FizzNodeEarning[];
  slashedAmount: string;
  slashedEras: string;
  isPaused: boolean;
};

export async function getFizzNodesByWallet(walletAddress: string): Promise<FizzNode[]> {
  const response = await fetchProtocolSubgraphData<{ fizzNodes: FizzNode[] }>(
    getFizzNodesByWalletQuery,
    { walletAddress: walletAddress.toLowerCase() }
  );
  return response.fizzNodes;
}
